import { useMemo, useState, isValidElement } from "react";
import {
  MapPin, User, Users, Wrench, AlertTriangle, Clock, Package,
  ShieldAlert, ClipboardCheck, PenLine, ChevronRight, X,
  ClipboardList, Sun, Moon, Zap, Flame, ArrowUp, HardHat,
  FlaskConical, Droplets,
} from "lucide-react";
import { signatureSrc } from "@/lib/signatures";
import { API_BASE } from "@/lib/api";

/**
 * resolvePhotoUrl — the production backend serves image references as
 * relative paths ("/api/photos/{pid}"). When those hit `<img src>` in the
 * portal preview domain the browser tries to fetch them from the portal
 * host, not the prod API, so every photo shows a broken-image icon.
 * Any absolute URL (http/https/data:) is returned untouched.
 */
function resolvePhotoUrl(u) {
  if (!u || typeof u !== "string") return u;
  if (/^(https?:|data:|blob:)/i.test(u)) return u;
  if (u.startsWith("/")) return `${API_BASE}${u}`;
  return u;
}

/**
 * ReportDocumentView — renders any Pit Link report as a professional
 * "digital document" that mirrors the printed PDF's visual language
 * (white paper card, orange section headers, chunky KV grids, embedded
 * signatures). This IS the visual standard for every certificate-style
 * page in the portal — PPF, Hazard, Breakdown, Handover, and anything
 * added later.
 *
 * =====================================================================
 * HOW TO ADD A NEW REPORT TYPE (e.g. "Fatigue Log", "Toolbox Talk"…)
 * =====================================================================
 *
 * There are two paths — pick whichever suits the new record's shape:
 *
 * A) BESPOKE (recommended for records with well-defined structure)
 *    1. Define a `MyThingDocument({ x })` function below alongside
 *       `PpfDocument`, `HazardDocument`, etc. Compose it from the
 *       exported primitives (`PaperShell`, `DocHeader`, `TwoCol`,
 *       `Col`, `DocSection`, `KvGrid`, `ProseBlock`, `DensePhotoGrid`,
 *       `SignatureCard`, `StatusChip`, `PillTile`, `ChipRow`).
 *    2. Route it here:  if (kind === "mything") return <MyThingDocument x={item} />;
 *    3. On the page component, pass:
 *          documentStyle
 *          extraDetail={<ReportDocumentView type="mything" item={detail} />}
 *
 * B) GENERIC (zero-effort — you get a doc view for free)
 *    Just pass `type="generic"` OR omit the type. The <GenericDocument>
 *    fallback auto-renders any object: it groups scalar fields into a
 *    KV grid, long-form strings into ProseBlocks, `photos` + `markups`
 *    into a DensePhotoGrid, and any *signature* / *sig* field into a
 *    SignatureCard. Great for prototyping a new resource without any
 *    layout work.
 *
 * Props:
 *   type: "ppf" | "hazard" | "breakdown" | "handover" | "generic" | <string>
 *   item: the full record
 *   config?: optional bespoke overrides for GenericDocument (badge,
 *            title, sections). See GenericDocument JSDoc.
 */
export default function ReportDocumentView({ type, item, config }) {
  if (!item) return null;
  const kind = String(type || "").toLowerCase();

  if (kind === "ppf")       return <PpfDocument ppf={item} />;
  if (kind === "hazard")    return <HazardDocument hz={item} />;
  if (kind === "breakdown") return <BreakdownDocument bd={item} />;
  if (kind === "handover")  return <HandoverDocument hd={item} />;
  // Fallback: any unknown type (or explicit "generic") renders through
  // the auto-layout. This is what makes the "same layout for future
  // sections" promise hold — no code change needed to get a doc view
  // for a new resource.
  return <GenericDocument item={item} type={kind || "record"} config={config} />;
}

// ---------------------------------------------------------------------------
// Shared paper primitives
// ---------------------------------------------------------------------------
function PaperShell({ children, testid }) {
  return (
    <article
      className="report-doc mx-auto w-full max-w-[1600px] shadow-2xl overflow-hidden rounded-2xl animate-in fade-in slide-in-from-bottom-4 duration-200"
      style={{
        background: "#fafafa",
        color: "#111",
        border: "1px solid #d9d9d9",
        boxShadow:
          "0 30px 80px -20px rgba(0,0,0,0.6), 0 8px 24px -10px rgba(245,130,32,0.25), 0 0 0 1px rgba(255,255,255,0.06) inset",
      }}
      data-testid={testid}
    >
      {children}
    </article>
  );
}

function TwoCol({ children }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] divide-neutral-200 lg:divide-x">
      {children}
    </div>
  );
}
function Col({ children, testid }) {
  return <div className="flex flex-col" data-testid={testid}>{children}</div>;
}

function DocHeader({ badge, title, subtitle, site, wo, statusChip }) {
  return (
    <header
      className="px-7 pt-5 pb-4"
      style={{ background: "linear-gradient(180deg, #ffffff 0%, #f4f4f4 100%)", borderBottom: "1px solid #e5e5e5" }}
    >
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md"
              style={{ background: "#f58220", color: "#fff", boxShadow: "0 4px 10px -4px rgba(245,130,32,0.6)" }}
              data-testid="doc-kind-badge"
            >
              <span className="text-[10px] uppercase tracking-[0.24em] font-black">{badge}</span>
            </div>
            {site && (
              <span className="text-[10px] uppercase tracking-widest font-black text-neutral-500">
                Site · <span className="text-neutral-800">{site}</span>
              </span>
            )}
            {statusChip}
          </div>
          <h1
            className="font-black tracking-tight"
            style={{ color: "#0a0a0a", fontSize: 24, lineHeight: 1.1, letterSpacing: "-0.02em" }}
            data-testid="doc-title"
          >
            {title}
          </h1>
          {subtitle && (
            <div className="mt-1.5 text-[12.5px] text-neutral-600" data-testid="doc-subtitle">
              {subtitle}
            </div>
          )}
        </div>
        {wo && (
          <div className="shrink-0 text-right" data-testid="doc-wo">
            <div className="text-[10px] uppercase tracking-[0.24em] font-black text-neutral-500">Work Order</div>
            <div className="font-black" style={{ color: "#f58220", fontSize: 22, letterSpacing: "-0.02em" }}>
              #{String(wo).trim()}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function DocSection({ icon: Icon, title, children, testid, tone = "orange", dense = false }) {
  const accent = tone === "red" ? "#c62828" : tone === "green" ? "#2e7d32" : "#f58220";
  return (
    <section className={`px-6 ${dense ? "py-2.5" : "py-3.5"} border-b border-neutral-200 last:border-b-0`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <span
          className="inline-block h-[3px] w-5 rounded-full"
          style={{ background: accent }}
        />
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent }} />}
        <h2
          className="font-black uppercase text-[10.5px] tracking-[0.22em]"
          style={{ color: "#1a1a1a" }}
        >
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function KvGrid({ rows, cols = 2, testid }) {
  // Rows accept plain scalars OR React elements (e.g. an icon + label).
  // Filter empty/null/blank scalars; React elements are always kept.
  const filtered = rows.filter(([, v]) => {
    if (v == null) return false;
    if (isValidElement(v)) return true;
    return String(v).trim() !== "";
  });
  if (!filtered.length) return null;
  const gridCols = cols === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2";
  return (
    <dl className={`grid grid-cols-1 ${gridCols} gap-x-5 gap-y-2`} data-testid={testid}>
      {filtered.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[9px] uppercase tracking-[0.18em] font-black text-neutral-500 mb-0.5">{k}</dt>
          <dd
            className="text-[13px] font-semibold text-neutral-900 truncate"
            title={isValidElement(v) ? undefined : String(v)}
          >
            {isValidElement(v) ? v : String(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ProseBlock({ label, value, tone = "default", testid }) {
  if (!value || !String(value).trim()) return null;
  const bg = tone === "red" ? "#fdecea" : "#fff";
  const border = tone === "red" ? "#f4b7b0" : "#e5e5e5";
  const label_color = tone === "red" ? "#c62828" : "#f58220";
  return (
    <div className="mb-2.5 last:mb-0" data-testid={testid}>
      <div
        className="text-[9.5px] uppercase tracking-[0.2em] font-black mb-1"
        style={{ color: label_color }}
      >
        {label}
      </div>
      <div
        className="rounded-lg px-3 py-2 text-[13px] leading-snug whitespace-pre-wrap"
        style={{ background: bg, border: `1px solid ${border}`, color: "#1a1a1a" }}
      >
        {String(value)}
      </div>
    </div>
  );
}

function RiskBadge({ level }) {
  if (!level) return null;
  const s = String(level).toLowerCase();
  const map = {
    critical: { bg: "#7f1d1d", fg: "#fff" },
    high:     { bg: "#c62828", fg: "#fff" },
    medium:   { bg: "#f58220", fg: "#fff" },
    low:      { bg: "#2e7d32", fg: "#fff" },
  };
  const c = map[s] || { bg: "#454545", fg: "#fff" };
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-black uppercase text-[10px] tracking-widest"
      style={{ background: c.bg, color: c.fg }}
      data-testid="doc-risk-badge"
    >
      <ShieldAlert className="w-3 h-3" /> {s}
    </span>
  );
}

function StatusChip({ label, tone }) {
  const map = {
    green:  { bg: "#e8f5e9", fg: "#2e7d32", brd: "#a5d6a7" },
    orange: { bg: "#fff3e0", fg: "#f58220", brd: "#ffcc80" },
    red:    { bg: "#fdecea", fg: "#c62828", brd: "#f4b7b0" },
    grey:   { bg: "#eeeeee", fg: "#555",    brd: "#d9d9d9" },
  }[tone] || { bg: "#eeeeee", fg: "#555", brd: "#d9d9d9" };
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-black uppercase text-[10px] tracking-widest"
      style={{ background: map.bg, color: map.fg, border: `1px solid ${map.brd}` }}
      data-testid="doc-status-chip"
    >
      <ChevronRight className="w-3 h-3" /> {label}
    </span>
  );
}

function SignatureCard({ role, name, when, sig }) {
  const src = signatureSrc(sig);
  return (
    <div
      className="rounded-xl overflow-hidden bg-white"
      style={{ border: "1px solid #e5e5e5" }}
    >
      <div className="px-3 pt-3 flex items-center justify-between">
        <span className="text-[9.5px] uppercase tracking-[0.2em] font-black text-neutral-500">{role}</span>
        <PenLine className="w-3 h-3 text-neutral-400" />
      </div>
      <div
        className="mx-3 mt-2 rounded-lg flex items-center justify-center"
        style={{ background: "#fff", border: "1px dashed #d9d9d9", minHeight: 90 }}
      >
        {src ? (
          <img src={src} alt={`${role} signature`} className="w-full h-auto max-h-32 object-contain" />
        ) : (
          <div className="py-6 text-neutral-400 text-[13px] italic">— unsigned —</div>
        )}
      </div>
      <div className="px-3 py-3">
        <div className="font-black text-[13px] text-neutral-900">{name || "—"}</div>
        {when && <div className="text-[10.5px] text-neutral-500 font-mono mt-0.5">{fmt(when)}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PPF Document
// ---------------------------------------------------------------------------
function PpfDocument({ ppf }) {
  const asset = ppf.asset_id || ppf.asset_name || "Asset";
  const fault = (ppf.fault || ppf.description || "").trim();
  const wo = (ppf.work_order_number || "").trim();
  const status = ppfStatusMeta(ppf);

  const parts = Array.isArray(ppf.parts_needed) ? ppf.parts_needed : [];
  const trades = useMemo(() => extractTrades(ppf), [ppf]);
  const totalTradeHours = trades.reduce((s, t) => s + (Number.isFinite(t.hours) ? t.hours : 0), 0);
  const tooling = toArr(ppf.tooling_required);
  const other = toArr(ppf.other_requirements);
  const partsPhotos = Array.isArray(ppf.parts_book_photos) ? ppf.parts_book_photos : [];
  const refPhotos = Array.isArray(ppf.photos) ? ppf.photos : [];

  return (
    <PaperShell testid="report-doc-ppf">
      <DocHeader
        badge="Planning Request"
        title={`Planning Request ${asset}${fault ? "  —  " + clip(fault, 90) : ""}`}
        subtitle={`Compliance pack · issued ${fmt(ppf.created_at) || "—"}`}
        site={ppf.site_code}
        wo={wo || null}
        statusChip={<StatusChip label={status.label} tone={status.tone} />}
      />

      <TwoCol>
        <Col testid="doc-ppf-col-left">
          <DocSection icon={ClipboardCheck} title="Machine Details" testid="doc-ppf-facts">
            <KvGrid rows={[
              ["Fleet Unit", ppf.asset_id],
              ["Priority",   ppf.priority],
              ["SMU (hrs)",  ppf.smu],
              ["People",     ppf.num_people],
              ["Est. Hours", ppf.estimated_hours],
              ["Specialist", ppf.specialist_skill],
              ["Target",     fmtDateOnly(ppf.target_completion)],
              ["Shift",      ppf.from_shift || ppf.shift],
            ]} />
          </DocSection>

          {(ppf.tradesman_name || ppf.crew_name || ppf.target_supervisor_name || ppf.supervisor_name) && (
            <DocSection icon={User} title="Personnel" testid="doc-ppf-people">
              <KvGrid rows={[
                ["Tradesman", joinWith(ppf.tradesman_name || ppf.created_by_name, ppf.tradesman_trade)],
                ["Crew",      ppf.crew_name],
                ["Assigned Supervisor", ppf.target_supervisor_name || ppf.supervisor_name || "— Any"],
              ]} />
            </DocSection>
          )}

          {trades.length > 0 && (
            <DocSection icon={Users} title={`Required Trades · Hours (${trades.length})`} testid="doc-ppf-trades">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">
                    <th className="text-left font-black pb-1.5 pr-2 border-b border-neutral-200">Trade</th>
                    <th className="text-right font-black pb-1.5 pr-2 border-b border-neutral-200">Qty</th>
                    <th className="text-right font-black pb-1.5 border-b border-neutral-200">Hrs</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t, i) => (
                    <tr key={i} style={{ background: i % 2 ? "#fafafa" : "#fff" }} data-testid={`doc-ppf-trade-${i}`}>
                      <td className="py-1.5 pr-2 font-bold text-neutral-900">{t.trade}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums text-neutral-800">
                        {Number.isFinite(t.qty) && t.qty > 0 ? t.qty : <span className="text-neutral-400">—</span>}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-neutral-900">
                        {Number.isFinite(t.hours) ? <>{t.hours}<span className="text-neutral-500"> h</span></> : <span className="text-neutral-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {totalTradeHours > 0 && (
                  <tfoot>
                    <tr>
                      <td className="pt-2 text-[9.5px] uppercase tracking-widest font-black text-neutral-500 border-t-2" style={{ borderColor: "#f58220" }}>Total</td>
                      <td className="pt-2 border-t-2" style={{ borderColor: "#f58220" }} />
                      <td className="pt-2 text-right tabular-nums font-black border-t-2" style={{ color: "#f58220", borderColor: "#f58220" }}>
                        {totalTradeHours}<span className="text-neutral-500"> h</span>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </DocSection>
          )}

          {parts.length > 0 && (
            <DocSection icon={Package} title={`Parts Needed · ${parts.length}`} testid="doc-ppf-parts">
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-[0.18em] text-neutral-500">
                      <th className="text-left font-black pb-1.5 pr-2 border-b border-neutral-200">Part #</th>
                      <th className="text-left font-black pb-1.5 pr-2 border-b border-neutral-200">Description</th>
                      <th className="text-right font-black pb-1.5 pr-2 border-b border-neutral-200">Qty</th>
                      <th className="text-left font-black pb-1.5 border-b border-neutral-200">Store</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parts.map((p, i) => (
                      <tr key={i} style={{ background: i % 2 ? "#fafafa" : "#fff" }} data-testid={`doc-ppf-part-${i}`}>
                        <td className="py-1.5 pr-2 font-mono font-bold text-neutral-900">{p.part_number || "—"}</td>
                        <td className="py-1.5 pr-2 text-neutral-800">{p.part_name || p.description || "—"}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums text-neutral-900">{p.quantity ?? "—"}</td>
                        <td className="py-1.5 text-neutral-700">{p.store_location || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DocSection>
          )}

          {(tooling.length > 0 || other.length > 0) && (
            <DocSection icon={Wrench} title="Tooling & Requirements" testid="doc-ppf-tooling" dense>
              {tooling.length > 0 && (
                <div className={other.length ? "mb-2" : ""}>
                  <div className="text-[9px] uppercase tracking-[0.2em] font-black text-neutral-500 mb-1">Tooling</div>
                  <ChipRow items={tooling} tone="orange" testid="doc-ppf-tooling-chips" />
                </div>
              )}
              {other.length > 0 && (
                <div>
                  <div className="text-[9px] uppercase tracking-[0.2em] font-black text-neutral-500 mb-1">Other</div>
                  <ChipRow items={other} tone="blue" testid="doc-ppf-other-chips" />
                </div>
              )}
            </DocSection>
          )}
        </Col>

        <Col testid="doc-ppf-col-right">
          {fault && (
            <DocSection icon={AlertTriangle} title="Problem Description" testid="doc-ppf-fault">
              <ProseBlock label="Fault" value={fault} testid="doc-ppf-fault-body" />
              {ppf.notes && <ProseBlock label="Notes" value={ppf.notes} />}
            </DocSection>
          )}

          {ppf.sent_back_reason && (
            <DocSection icon={AlertTriangle} title="Sent Back" tone="red" testid="doc-ppf-sendback">
              <ProseBlock label="Reason" value={ppf.sent_back_reason} tone="red" />
            </DocSection>
          )}

          {partsPhotos.length > 0 && (
            <DocSection icon={Package} title={`Parts Book Photos · ${partsPhotos.length}`} testid="doc-ppf-parts-photos" dense>
              <DensePhotoGrid items={partsPhotos} />
            </DocSection>
          )}

          {refPhotos.length > 0 && (
            <DocSection icon={Package} title={`Reference Photos · ${refPhotos.length}`} testid="doc-ppf-refphotos" dense>
              <DensePhotoGrid items={refPhotos} />
            </DocSection>
          )}

          <DocSection icon={PenLine} title="Signatures" testid="doc-ppf-sigs" dense>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <SignatureCard
                role="Submitted by"
                name={ppf.tradesman_name || ppf.created_by_name}
                when={ppf.submitted_at || ppf.created_at}
                sig={ppf.tradesperson_sig || ppf.submitted_by_sig}
              />
              <SignatureCard
                role="Completed / Approved"
                name={ppf.completed_by_name || ppf.supervisor_name || ppf.approver_name}
                when={ppf.completed_at || ppf.approved_at}
                sig={ppf.completed_by_sig || ppf.supervisor_sig}
              />
            </div>
          </DocSection>
        </Col>
      </TwoCol>
    </PaperShell>
  );
}

// ---------------------------------------------------------------------------
// Hazard Document
// ---------------------------------------------------------------------------
function HazardDocument({ hz }) {
  const desc = (hz.description || hz.summary || "").trim();
  const status = hazardStatusMeta(hz);
  const photos = Array.isArray(hz.photos) ? hz.photos : Array.isArray(hz.photo_urls) ? hz.photo_urls : [];
  const markups = Array.isArray(hz.markups) ? hz.markups : [];
  const photoCount = photos.length + markups.length;

  return (
    <PaperShell testid="report-doc-hazard">
      <DocHeader
        badge="SITE HAZARD NOTIFICATION"
        title={desc ? clip(desc, 110) : "Site Hazard"}
        subtitle={`Reported ${fmt(hz.created_at || hz.reported_at) || "—"}`}
        site={hz.site_code}
        statusChip={
          <>
            <StatusChip label={status.label} tone={status.tone} />
            <RiskBadge level={hz.risk_level || hz.severity} />
          </>
        }
      />

      <TwoCol>
        <Col testid="doc-hz-col-left">
          <DocSection icon={MapPin} title="Location & Context" testid="doc-hz-facts">
            <KvGrid rows={[
              ["Location",              hz.location],
              ["Hazard Classification", hz.classification || hz.category],
              ["Risk Assessment Level", hz.risk_level || hz.severity],
              ["Area Isolation Required", (hz.area_isolation_required ?? hz.isolation_required) === true ? "Yes" : (hz.area_isolation_required === false ? "No" : null)],
              ["Shift",                 hz.shift],
              ["Crew",                  hz.crew_name || hz.crew],
              ["Responsible Person",    hz.tradesman_name || hz.created_by_name || hz.reported_by],
              ["Assigned Supervisor",   hz.assigned_supervisor_name || hz.supervisor_name],
              ["Reported At",           fmt(hz.created_at || hz.reported_at)],
              ["Signed Off",            fmt(hz.actioned_at || hz.closed_at || hz.approved_at)],
            ]} />
          </DocSection>

          <DocSection icon={PenLine} title="Signatures" testid="doc-hz-sigs" dense>
            <div className="grid grid-cols-1 gap-2">
              <SignatureCard
                role="Responsible Person"
                name={hz.tradesman_name || hz.created_by_name || hz.reported_by}
                when={hz.created_at || hz.reported_at}
                sig={hz.reporter_sig || hz.tradesperson_sig}
              />
              <SignatureCard
                role="Signed off by"
                name={hz.supervisor_name || hz.actioned_by_name}
                when={hz.actioned_at || hz.closed_at || hz.approved_at}
                sig={hz.supervisor_sig || hz.signature_svg || hz.sign_off_signature}
              />
            </div>
          </DocSection>
        </Col>

        <Col testid="doc-hz-col-right">
          {desc && (
            <DocSection icon={AlertTriangle} title="Risk Description" testid="doc-hz-desc">
              <ProseBlock label="Risk Description" value={desc} />
            </DocSection>
          )}

          {(hz.immediate_controls || hz.controls_implemented) && (
            <DocSection icon={ShieldAlert} title="Immediate Controls Implemented" testid="doc-hz-controls">
              <ProseBlock label="Immediate Controls Implemented" value={hz.immediate_controls || hz.controls_implemented} />
            </DocSection>
          )}

          {(hz.corrective_action || hz.suggested_corrective || hz.notes) && (
            <DocSection icon={Wrench} title="Mitigation Strategy" testid="doc-hz-corrective">
              <ProseBlock label="Mitigation Strategy" value={hz.corrective_action || hz.suggested_corrective} />
              {hz.notes && <ProseBlock label="Notes" value={hz.notes} />}
            </DocSection>
          )}

          {photoCount > 0 && (
            <DocSection icon={Package} title={`Evidence Photos · ${photoCount}`} testid="doc-hz-photos" dense>
              <DensePhotoGrid items={{ photos, markups }} />
            </DocSection>
          )}
        </Col>
      </TwoCol>
    </PaperShell>
  );
}

// ---------------------------------------------------------------------------
// Breakdown Document
// ---------------------------------------------------------------------------
function BreakdownDocument({ bd }) {
  const asset = bd.asset_id || bd.asset_name || "Asset";
  const status = breakdownStatusMeta(bd);
  const photos = Array.isArray(bd.photos) ? bd.photos : Array.isArray(bd.photo_urls) ? bd.photo_urls : [];
  const markups = Array.isArray(bd.markups) ? bd.markups : [];
  const photoCount = photos.length + markups.length;
  const wo = (bd.work_order_number || "").trim();
  const complaintLead = clip(bd.complaint || bd.cause || "Breakdown", 90);
  const dt = computeDowntime(bd);
  const hazards = Array.isArray(bd.hazards) ? bd.hazards.filter(Boolean) : [];
  const waitingParts = Array.isArray(bd.waiting_parts) ? bd.waiting_parts.filter(Boolean) : [];
  // Asset serviceability — supersedes the old "good to go? yes/no" line.
  // Josh (Feb 26): "make the toggle look like a high-end engineering
  // decision, not a basic yes/no checkbox." Green = released for
  // production, red/orange = stood down.
  const released = !!(bd.back_in_service_time || bd.is_finalised);
  const authorisedBy = bd.approved_by_name || bd.locked_by_name || bd.supervisor_name || bd.completed_by_name || "—";

  return (
    <PaperShell testid="report-doc-breakdown">
      <DocHeader
        badge="ASSET BREAKDOWN REPORT"
        title={`${asset}${complaintLead ? "  —  " + complaintLead : ""}`}
        subtitle={`Maintenance pack · logged ${fmt(bd.breakdown_time || bd.created_at) || "—"}`}
        site={bd.site_code}
        wo={wo || null}
        statusChip={<StatusChip label={status.label} tone={status.tone} />}
      />

      {/* Asset Serviceability Status — flagship decision card */}
      <ServiceabilityCard released={released} authorisedBy={authorisedBy} />

      <DocSection icon={Clock} title="Downtime Duration" testid="doc-bd-downtime" dense>
        <div className="grid grid-cols-3 gap-2">
          <PillTile label="Down at"         value={fmt(bd.breakdown_time || bd.started_at)} />
          <PillTile label="Back in service" value={fmt(bd.back_in_service_time || bd.finished_at)} />
          <PillTile label="Total downtime"  value={dt || bd.downtime_duration || "—"} accent />
        </div>
      </DocSection>

      <TwoCol>
        <Col testid="doc-bd-col-left">
          <DocSection icon={ClipboardCheck} title="Machine Details" testid="doc-bd-facts">
            <KvGrid rows={[
              ["Fleet Unit", bd.asset_id],
              ["Component Affected", bd.subcategory || bd.component],
              ["Crew",       bd.crew_name],
              ["SMU (hrs)",  bd.smu],
              ["Priority",   bd.priority || bd.severity],
              ["Location",   bd.location],
              ["Tradesperson", bd.employee_name || bd.tradesman_name],
              ["Supervisor", bd.supervisor_name || bd.assigned_supervisor_name],
              ["Authorised By", bd.approved_by_name || bd.locked_by_name],
              ["Work Order #", bd.work_order_number],
              ["Reported At", fmt(bd.created_at)],
              ["Approved At", fmt(bd.approved_at)],
            ]} />
          </DocSection>

          {hazards.length > 0 && (
            <DocSection icon={ShieldAlert} title={`Site Hazards · ${hazards.length}`} testid="doc-bd-hazards" dense>
              <ChipRow items={hazards.map((h) => typeof h === "string" ? h : (h.label || h.id || "Hazard"))} tone="orange" testid="doc-bd-hazard-chips" />
            </DocSection>
          )}

          {waitingParts.length > 0 && (
            <DocSection icon={Package} title={`Parts Required · ${waitingParts.length}`} testid="doc-bd-waiting" dense>
              <ChipRow
                items={waitingParts.map((p) => typeof p === "string" ? p
                  : `${p.name || p.part_name || "Part"}${p.part_number ? " · #" + p.part_number : ""}${p.quantity ? " × " + p.quantity : ""}${p.eta ? " · ETA " + p.eta : ""}`)}
                tone="red"
                testid="doc-bd-waiting-chips"
              />
            </DocSection>
          )}

          {(bd.is_recurring !== undefined ||
            bd.requires_next_service !== undefined ||
            bd.requires_incoming_crew !== undefined) && (
            <DocSection icon={ClipboardCheck} title="Follow-up Flags" testid="doc-bd-status">
              <div className="grid grid-cols-1 gap-2" data-testid="doc-bd-status-rows">
                <StatusYNRow
                  label="Requires further repairs next service?"
                  yes={!!bd.requires_next_service}
                  testid="doc-bd-status-nextsvc"
                />
                <StatusYNRow
                  label="Recurring fault?"
                  yes={!!bd.is_recurring}
                  testid="doc-bd-status-recurring"
                />
                <StatusYNRow
                  label="Requires incoming crew work?"
                  yes={!!bd.requires_incoming_crew}
                  testid="doc-bd-status-incoming"
                />
              </div>
            </DocSection>
          )}

          {(bd.part_name || bd.part_number || bd.quantity) && (
            <DocSection icon={Package} title="Parts Used" testid="doc-bd-parts" dense>
              <KvGrid rows={[
                ["Part Number", bd.part_number],
                ["Part Name",   bd.part_name],
                ["Quantity",    bd.quantity],
                ["Subcategory", bd.subcategory],
              ]} />
            </DocSection>
          )}

          <DocSection icon={PenLine} title="Signatures" testid="doc-bd-sigs" dense>
            <div className="grid grid-cols-1 gap-2">
              <SignatureCard
                role="Tradesperson"
                name={bd.employee_name || bd.tradesman_name || bd.created_by_name}
                when={bd.breakdown_time || bd.created_at}
                sig={bd.tradesperson_sig || bd.employee_sig}
              />
              <SignatureCard
                role="Authorised by"
                name={bd.approved_by_name || bd.locked_by_name || bd.supervisor_name || bd.completed_by_name}
                when={bd.approved_at || bd.updated_at}
                sig={bd.supervisor_sig || bd.completed_by_sig}
              />
            </div>
          </DocSection>
        </Col>

        <Col testid="doc-bd-col-right">
          {(bd.complaint || bd.cause || bd.corrective_action || bd.comments || bd.notes) && (
            <DocSection icon={AlertTriangle} title="Failure Description" testid="doc-bd-fault">
              {bd.complaint && <ProseBlock label="Failure Description" value={bd.complaint} />}
              {bd.cause && <ProseBlock label="Root Cause" value={bd.cause} />}
              {bd.corrective_action && <ProseBlock label="Repair Actions" value={bd.corrective_action} />}
              {(bd.comments || bd.notes) && <ProseBlock label="Comments" value={bd.comments || bd.notes} />}
            </DocSection>
          )}

          {photoCount > 0 && (
            <DocSection icon={Package} title={`Evidence Photos · ${photoCount}`} testid="doc-bd-photos" dense>
              <DensePhotoGrid items={{ photos, markups }} />
            </DocSection>
          )}

          {bd.sent_back_reason && (
            <DocSection icon={AlertTriangle} title="Sent Back" tone="red" testid="doc-bd-sendback">
              <ProseBlock label="Reason" value={bd.sent_back_reason} tone="red" />
            </DocSection>
          )}
        </Col>
      </TwoCol>
    </PaperShell>
  );
}

// ---------------------------------------------------------------------------
// ServiceabilityCard — flagship "released for production" vs "stood down"
// engineering-decision card. Sits at the top of every breakdown report so
// the outgoing supervisor can see the state at a glance without hunting
// through the yes/no rows. Josh (Feb 26): "Make the toggle look like a
// high-end engineering decision, not a basic yes/no checkbox."
// ---------------------------------------------------------------------------
function ServiceabilityCard({ released, authorisedBy }) {
  const label = released ? "RELEASED FOR OPERATIONS" : "STOOD DOWN — REQUIRES REPAIR";
  const accent = released
    ? { bg: "#e8f7ec", border: "#4caf50", chip: "#2e7d32", ring: "0 0 0 1px #4caf50 inset" }
    : { bg: "#fef4ee", border: "#f97316", chip: "#c2410c", ring: "0 0 0 1px #f97316 inset" };
  return (
    <div className="mb-4" data-testid="doc-bd-serviceability">
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: "#fff", border: `1px solid ${accent.border}`, boxShadow: accent.ring }}
      >
        <div
          className="px-4 py-2 flex items-center justify-between gap-3"
          style={{ background: accent.bg, borderBottom: `1px solid ${accent.border}` }}
        >
          <span
            className="text-[10px] font-black uppercase tracking-[0.24em]"
            style={{ color: accent.chip }}
          >
            Asset Serviceability Status
          </span>
          <span
            className="text-[10px] font-black uppercase tracking-[0.16em] flex items-center gap-1.5"
            style={{ color: accent.chip }}
          >
            {released ? <ClipboardCheck className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            Engineering decision
          </span>
        </div>
        <div className="px-4 py-4 flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: accent.bg, border: `1px solid ${accent.border}` }}
          >
            {released ? (
              <ClipboardCheck className="w-7 h-7" style={{ color: accent.chip }} />
            ) : (
              <AlertTriangle className="w-7 h-7" style={{ color: accent.chip }} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[19px] font-black leading-tight tracking-tight" style={{ color: "#111" }}>
              {label}
            </div>
            <div className="text-[11px] font-mono-tight mt-1.5" style={{ color: "#555" }}>
              Authorised By: <span className="font-black" style={{ color: "#111" }}>{authorisedBy || "—"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Handover Document — mirrors mobile app "Shift Handover" record.
// ---------------------------------------------------------------------------
const HANDOVER_HAZARD_ICONS = {
  "stored-energy": Zap,
  "hot-work": Flame,
  "height": ArrowUp,
  "confined": HardHat,
  "chemicals": FlaskConical,
  "wet-slippery": Droplets,
  "moving-parts": Wrench,
};

function HandoverDocument({ hd }) {
  const asset = hd.asset_id || "Asset";
  const shift = String(hd.shift || "").toLowerCase();
  const ShiftIcon = shift.includes("night") ? Moon : Sun;
  const photos = Array.isArray(hd.photos) ? hd.photos : [];
  const markups = Array.isArray(hd.markups) ? hd.markups : [];
  const photoCount = photos.length + markups.length;
  const hazards = Array.isArray(hd.hazards) ? hd.hazards : [];
  const status = { label: "Filed", tone: "green" };
  const lead = clip(hd.breakdown_fault || hd.work_completed || hd.machine_status || "Shift handover", 100);

  return (
    <PaperShell testid="report-doc-handover">
      <DocHeader
        badge={`OPERATIONAL SHIFT HANDOVER · ${shift.toUpperCase() || "SHIFT"}`}
        title={`${asset}${lead ? "  —  " + lead : ""}`}
        subtitle={`Operational briefing · filed ${fmt(hd.created_at) || fmt(hd.date) || "—"}`}
        site={hd.site_code}
        statusChip={<StatusChip label={status.label} tone={status.tone} />}
      />

      <TwoCol>
        <Col testid="doc-hd-col-left">
          <DocSection icon={ClipboardCheck} title="Shift Details" testid="doc-hd-facts">
            <KvGrid rows={[
              ["Fleet Unit", hd.asset_id],
              ["SMU (hrs)",  hd.smu],
              ["Shift",      hd.shift && (<span className="inline-flex items-center gap-1.5"><ShiftIcon className="w-3.5 h-3.5" /> {hd.shift}</span>)],
              ["Date",       fmtDateOnly(hd.date) || fmtDateOnly(hd.created_at)],
              ["Tradesman",  hd.tradesman || hd.created_by_name],
              ["Crew",       hd.crew_name],
              ["Filed at",   fmt(hd.created_at)],
            ]} />
          </DocSection>

          {hazards.length > 0 && (
            <DocSection icon={ShieldAlert} title={`Site Hazards · ${hazards.length}`} testid="doc-hd-hazards" dense>
              <div className="flex flex-wrap gap-1.5">
                {hazards.map((h, i) => {
                  const label = typeof h === "string" ? h : (h.label || h.id || "Hazard");
                  const id = typeof h === "string" ? h : h.id;
                  const Icon = HANDOVER_HAZARD_ICONS[id] || ShieldAlert;
                  return (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-bold"
                      style={{ background: "#fff3e0", color: "#c76a00", border: "1px solid #ffcc80" }}
                      data-testid={`doc-hd-hazard-${i}`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </span>
                  );
                })}
              </div>
            </DocSection>
          )}

          <DocSection icon={PenLine} title="Signature" testid="doc-hd-sig" dense>
            <SignatureCard
              role="Signed off by"
              name={hd.sign_off_name || hd.tradesman || hd.created_by_name}
              when={hd.created_at}
              sig={hd.sign_off_signature || hd.tradesperson_sig}
            />
          </DocSection>
        </Col>

        <Col testid="doc-hd-col-right">
          {(hd.breakdown_fault || hd.work_completed || hd.machine_status ||
            hd.operator_complaint || hd.delays_parts || hd.next_priorities ||
            hd.safety_issues) && (
            <DocSection icon={ClipboardList} title="Shift Notes" testid="doc-hd-notes">
              {hd.breakdown_fault && <ProseBlock label="Breakdown Fault" value={hd.breakdown_fault} tone="red" />}
              {hd.work_completed  && <ProseBlock label="Work Completed" value={hd.work_completed} />}
              {hd.machine_status  && <ProseBlock label="Machine Status" value={hd.machine_status} />}
              {hd.operator_complaint && <ProseBlock label="Operator Complaint" value={hd.operator_complaint} />}
              {hd.delays_parts    && <ProseBlock label="Delays / Parts" value={hd.delays_parts} />}
              {hd.next_priorities && <ProseBlock label="Next Priorities" value={hd.next_priorities} />}
              {hd.safety_issues   && <ProseBlock label="Safety Issues" value={hd.safety_issues} tone="red" />}
            </DocSection>
          )}

          {photoCount > 0 && (
            <DocSection icon={Package} title={`Evidence Photos · ${photoCount}`} testid="doc-hd-photos" dense>
              <DensePhotoGrid items={{ photos, markups }} />
            </DocSection>
          )}
        </Col>
      </TwoCol>
    </PaperShell>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
function PillTile({ label, value, accent }) {
  return (
    <div
      className="rounded-lg px-3 py-2.5"
      style={{
        background: accent ? "#fff3e0" : "#fff",
        border: `1px solid ${accent ? "#ffcc80" : "#e5e5e5"}`,
      }}
    >
      <div className="text-[9.5px] uppercase tracking-[0.18em] font-black"
           style={{ color: accent ? "#c76a00" : "#666" }}>
        {label}
      </div>
      <div className="text-[13px] font-mono font-bold mt-0.5" style={{ color: "#111" }}>
        {value || "—"}
      </div>
    </div>
  );
}


/**
 * StatusYNRow — a mobile-app-parity YES/NO question row used in the
 * Breakdown "Status" section. Same layout as the mobile screen (label
 * left, YES / NO pill pair right, chosen answer safety-orange) but
 * colour-adapted to the white certificate paper so it sits alongside
 * the rest of the doc without clashing. Josh Feb 23 spec.
 */
function StatusYNRow({ label, yes, testid }) {
  const activeStyle = { background: "#f97316", color: "#fff", boxShadow: "0 2px 6px rgba(249,115,22,0.35)" };
  const idleStyle = { background: "#f1f1f1", color: "#8a8a8a", border: "1px solid #e5e5e5" };
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-lg px-4 py-3"
      style={{
        background: "#fff",
        border: "1px solid #e5e5e5",
      }}
      data-testid={testid}
    >
      <div className="text-[13px] font-semibold text-neutral-900 pr-2">{label}</div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span
          className="px-4 py-1.5 rounded-md text-[11px] tracking-widest font-black"
          style={yes ? activeStyle : idleStyle}
          data-testid={`${testid}-yes`}
        >YES</span>
        <span
          className="px-4 py-1.5 rounded-md text-[11px] tracking-widest font-black"
          style={!yes ? activeStyle : idleStyle}
          data-testid={`${testid}-no`}
        >NO</span>
      </div>
    </div>
  );
}

function ChipRow({ items, tone = "orange", testid }) {
  const colors = tone === "blue"
    ? { bg: "#e3f2fd", brd: "#90caf9", fg: "#1565c0" }
    : { bg: "#fff3e0", brd: "#ffcc80", fg: "#c76a00" };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {items.map((t, i) => (
        <span
          key={i}
          className="text-[11.5px] font-bold px-2 py-1 rounded-md"
          style={{ background: colors.bg, color: colors.fg, border: `1px solid ${colors.brd}` }}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

// Compact photo tile grid — 5-col responsive, click-to-zoom lightbox.
// Accepts either flat photo entries OR an object `{ photos, markups }` so
// technician mark-ups (circled damage / annotations) are shown alongside
// the raw photos with a "MARKED" chip. Markups can carry an SVG overlay
// (`overlay_svg`) which we composite over the base image so the drawn
// circles/arrows are visible in the portal.
function DensePhotoGrid({ items }) {
  const [zoom, setZoom] = useState(null); // { url, svg, annotated } or null
  const pickUrl = (it) => {
    if (!it) return null;
    const raw = typeof it === "string" ? it : (
      it.base_image_uri ||
      it.annotated_url ||
      it.marked_up_url ||
      it.annotated ||
      it.markup_url ||
      it.overlay_url ||
      it.image_url ||
      it.image ||
      it.url ||
      it.data ||
      it.photo_url ||
      it.href ||
      it.src ||
      null
    );
    return resolvePhotoUrl(raw);
  };
  const pickSvg = (it) => (it && typeof it === "object" ? (it.overlay_svg || it.svg || it.markup_svg || null) : null);
  // Normalise into a tagged list. Support two call shapes:
  //   <DensePhotoGrid items={photosArr} />
  //   <DensePhotoGrid items={{ photos: [...], markups: [...] }} />
  const tagged = [];
  const seen = new Set();
  const push = (raw, annotated) => {
    const u = pickUrl(raw);
    if (!u) return;
    const svg = pickSvg(raw);
    const key = u + "|" + (svg ? svg.length : 0);
    if (seen.has(key)) return;
    seen.add(key);
    tagged.push({ url: u, svg, annotated });
  };
  if (items && !Array.isArray(items) && (items.photos || items.markups)) {
    (items.markups || []).forEach((m) => push(m, true));
    (items.photos || []).forEach((p) => {
      const u = pickUrl(p);
      const alreadyMarked = tagged.some((x) => x.url === u && x.svg);
      if (alreadyMarked) return;
      push(p, false);
    });
  } else {
    (items || []).forEach((it) => push(it, false));
  }
  if (!tagged.length) return null;
  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-1.5" data-testid="doc-photo-grid">
        {tagged.map((p, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setZoom(p)}
            className="relative aspect-square rounded-md overflow-hidden bg-neutral-900 transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-orange-500"
            style={{ border: "1px solid #e5e5e5" }}
            data-testid={`doc-photo-${i}`}
            title={p.annotated ? "Marked-up photo — click to zoom" : "Click to zoom"}
          >
            <img src={p.url} alt="" className="w-full h-full object-contain" />
            {p.svg && (
              <div
                className="absolute inset-0 pointer-events-none"
                dangerouslySetInnerHTML={{ __html: p.svg.replace(/<svg([^>]*)>/, '<svg$1 style="width:100%;height:100%;">') }}
              />
            )}
            {p.annotated && (
              <span
                className="absolute top-1 left-1 px-1.5 py-0.5 rounded-sm text-[9px] tracking-wider font-black shadow z-10"
                style={{ background: "#f97316", color: "#000" }}
                data-testid={`doc-photo-marked-${i}`}
              >
                MARKED
              </span>
            )}
          </button>
        ))}
      </div>
      {zoom && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85"
          onClick={() => setZoom(null)}
          data-testid="doc-photo-zoom"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoom(null); }}
            className="absolute top-6 right-6 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="relative"
            style={{ width: "min(95vw,1400px)", height: "min(90vh,900px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={zoom.url}
              alt=""
              className="w-full h-full object-contain rounded-lg shadow-2xl"
              style={{ background: "#0a0a0a" }}
            />
            {zoom.svg && (
              <div
                className="absolute inset-0 pointer-events-none"
                dangerouslySetInnerHTML={{ __html: zoom.svg.replace(/<svg([^>]*)>/, '<svg$1 style="width:100%;height:100%;">') }}
              />
            )}
            {zoom.annotated && (
              <span
                className="absolute top-3 left-3 px-2 py-1 rounded text-[10px] tracking-widest font-black shadow-lg z-10"
                style={{ background: "#f97316", color: "#000" }}
              >
                MARKED BY TECHNICIAN
              </span>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function extractTrades(ppf) {
  if (Array.isArray(ppf.trades_needed) && ppf.trades_needed.length) {
    return ppf.trades_needed.map((t) => ({
      trade: t.trade || t.role || "",
      hours: t.hours != null && t.hours !== "" ? Number(t.hours) : null,
    })).filter((t) => t.trade);
  }
  // Legacy `human_resources_list` may be either an array of strings OR an
  // array of objects — production ships the object shape `{trade, qty, hours}`.
  if (Array.isArray(ppf.human_resources_list) && ppf.human_resources_list.length) {
    return ppf.human_resources_list.map((raw) => {
      if (raw && typeof raw === "object") {
        return {
          trade: String(raw.trade || raw.role || "").trim(),
          hours: raw.hours != null && raw.hours !== "" ? Number(raw.hours) : null,
          qty: raw.qty != null && raw.qty !== "" ? Number(raw.qty) : null,
        };
      }
      const m = String(raw).match(/^(.+?)[\u00B7·:\-]+\s*([\d.]+)\s*(h|hr|hrs)?$/i);
      if (m) return { trade: m[1].trim(), hours: Number(m[2]) };
      return { trade: String(raw).trim(), hours: null };
    }).filter((t) => t.trade);
  }
  if (typeof ppf.human_resources === "string" && ppf.human_resources.trim()) {
    return ppf.human_resources.split(",").map((raw) => {
      const m = String(raw).match(/^(.+?)[\u00B7·:\-]+\s*([\d.]+)\s*(h|hr|hrs)?$/i);
      if (m) return { trade: m[1].trim(), hours: Number(m[2]) };
      return { trade: String(raw).trim(), hours: null };
    }).filter((t) => t.trade);
  }
  return [];
}

function toArr(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim()) return v.split(/[,;]/).map((x) => x.trim()).filter(Boolean);
  return [];
}

function joinWith(a, b) {
  if (a && b) return `${a} · ${b}`;
  return a || b || null;
}

function clip(s, n) {
  const str = String(s || "");
  return str.length > n ? str.slice(0, n - 1) + "…" : str;
}

function fmt(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function fmtDateOnly(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleDateString(); } catch { return String(v); }
}

function computeDowntime(bd) {
  if (bd.downtime_duration) return bd.downtime_duration;
  const d1 = bd.breakdown_time || bd.started_at;
  const d2 = bd.back_in_service_time || bd.finished_at;
  if (!d1 || !d2) return null;
  const t1 = new Date(String(d1).replace(" ", "T"));
  const t2 = new Date(String(d2).replace(" ", "T"));
  if (isNaN(t1) || isNaN(t2) || t2 <= t1) return null;
  const mins = Math.round((t2 - t1) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function ppfStatusMeta(ppf) {
  const s = String(ppf.status || ppf.stage || "").toLowerCase();
  if (ppf.approved_at || s.includes("approv") || s.includes("complete") || s.includes("done") || s.includes("closed")) {
    return { label: "Approved", tone: "green" };
  }
  if (s.includes("send") || s.includes("back") || ppf.sent_back_reason) {
    return { label: "Sent Back", tone: "red" };
  }
  if (s.includes("submit") || s.includes("pending") || s.includes("await")) {
    return { label: "Awaiting Sign-off", tone: "orange" };
  }
  if (s.includes("draft")) return { label: "Draft", tone: "grey" };
  return { label: s ? s.toUpperCase() : "Open", tone: "orange" };
}

function hazardStatusMeta(hz) {
  const s = String(hz.status || "").toLowerCase();
  if (s === "actioned" || s === "closed" || hz.actioned_at || hz.closed_at) {
    return { label: "Signed Off", tone: "green" };
  }
  return { label: "Pending Sign-off", tone: "orange" };
}

function breakdownStatusMeta(bd) {
  const s = String(bd.status || "").toLowerCase();
  if (bd.approved_at || s === "approved") return { label: "Approved", tone: "green" };
  if (bd.is_finalised) return { label: "Awaiting Sign-off", tone: "orange" };
  if (bd.back_in_service_time || bd.finished_at) return { label: "Back in Service", tone: "orange" };
  return { label: "Open", tone: "red" };
}

// Also export the sub-view type constant list for tests / instrumentation.
export const REPORT_DOC_TYPES = ["ppf", "hazard", "breakdown", "handover", "generic"];

// -------------------------------------------------------------------------
// Primitive exports — compose these to author a bespoke document view.
// Any new report type should re-use these instead of inventing its own
// layout so every screen stays visually consistent.
// -------------------------------------------------------------------------
export {
  PaperShell,
  DocHeader,
  TwoCol,
  Col,
  DocSection,
  KvGrid,
  ProseBlock,
  DensePhotoGrid,
  SignatureCard,
  StatusChip,
  RiskBadge,
  PillTile,
  ChipRow,
  resolvePhotoUrl,
  fmt,
  fmtDateOnly,
  clip,
};

// -------------------------------------------------------------------------
// GenericDocument — auto-layout fallback used when the caller passes an
// unknown `type` (or `type="generic"`). Turns any object into the standard
// white-paper document layout with zero configuration:
//
//   • Scalar string/number fields → KvGrid rows (left column)
//   • Long text (> 60 chars or explicit description-ish keys) → ProseBlock
//   • `photos` / `photo_urls` / `attachments` + `markups` → DensePhotoGrid
//   • Any *_sig / *_signature or `sign_off_signature` → SignatureCard
//   • Any *hazards* array of strings/objects → orange chip row
//   • `sent_back_reason` / `error` / `rejection` → red-tinted callout
//
// Optionally accepts a `config` prop to override the auto-detected labels
// and grouping without writing a full bespoke component:
//
//   config = {
//     badge:    "Toolbox Talk",              // header pill text
//     title:    (it) => `Talk · ${it.topic}`,// header main heading
//     subtitle: (it) => `Filed ${fmt(it.created_at)}`,
//     status:   (it) => ({ label: "Filed", tone: "green" }),
//     leftFields:  ["asset_id", "smu", "shift", "…"],   // KV order in left col
//     rightProse:  ["complaint", "corrective_action"],   // prose blocks
//     hidden:      ["_id", "site_code", "client_op_id"], // never show
//   }
// -------------------------------------------------------------------------
const PROSE_HINT_KEYS = new Set([
  "description", "notes", "comments", "complaint", "cause", "fault",
  "corrective_action", "resolution", "summary", "message", "reason",
  "work_completed", "machine_status", "operator_complaint",
  "delays_parts", "next_priorities", "safety_issues", "breakdown_fault",
]);
const SIG_HINT_RX = /(sig|signature)$/i;
const HAZARD_HINT_RX = /^hazard(s)?$/i;
const PHOTO_HINT_KEYS = new Set(["photos", "photo_urls", "attachments", "images"]);
const HIDDEN_BY_DEFAULT = new Set([
  "id", "_id", "client_op_id", "created_by_id", "crew_id",
  "site_code", "site_id", "template_id", "markups", "history",
  "items_state", "sections",
]);
const HUMAN_LABEL = (k) => String(k || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function GenericDocument({ item, type = "record", config = {} }) {
  const cfg = config || {};
  const hidden = new Set([...HIDDEN_BY_DEFAULT, ...(cfg.hidden || [])]);

  // Bucket the item's keys.
  const kvRows = [];
  const proseRows = [];
  let photosField = null;
  let markupsField = null;
  const sigRows = [];
  const hazardChips = [];
  let sentBackReason = null;

  Object.entries(item || {}).forEach(([k, v]) => {
    if (hidden.has(k)) return;
    if (v == null || v === "") return;

    // Sign-off flags
    if (SIG_HINT_RX.test(k) && typeof v === "string" && v.length > 40) {
      sigRows.push({ key: k, value: v });
      return;
    }
    // Photos / markups
    if (PHOTO_HINT_KEYS.has(k) && Array.isArray(v)) { photosField = v; return; }
    if (k === "markups" && Array.isArray(v)) { markupsField = v; return; }
    // Hazards chip row
    if (HAZARD_HINT_RX.test(k) && Array.isArray(v)) {
      v.forEach((h) => hazardChips.push(typeof h === "string" ? h : (h.label || h.id || "Hazard")));
      return;
    }
    if (k === "sent_back_reason") { sentBackReason = String(v); return; }

    // Prose (long text or hint key)
    if (PROSE_HINT_KEYS.has(k) || (typeof v === "string" && v.length > 60)) {
      proseRows.push({ label: cfg.labels?.[k] || HUMAN_LABEL(k), value: String(v), key: k });
      return;
    }
    // Fallback → KV
    const label = cfg.labels?.[k] || HUMAN_LABEL(k);
    kvRows.push([label, formatValue(v)]);
  });

  // Optional caller reordering of the left column KV rows.
  if (Array.isArray(cfg.leftFields) && cfg.leftFields.length) {
    kvRows.sort((a, b) => rank(cfg.leftFields, a[0]) - rank(cfg.leftFields, b[0]));
  }
  // Optional caller reordering of the right column prose blocks.
  if (Array.isArray(cfg.rightProse) && cfg.rightProse.length) {
    proseRows.sort((a, b) => rank(cfg.rightProse.map(HUMAN_LABEL), a.label) - rank(cfg.rightProse.map(HUMAN_LABEL), b.label));
  }

  const badge = typeof cfg.badge === "function" ? cfg.badge(item) : (cfg.badge || HUMAN_LABEL(type));
  const title = typeof cfg.title === "function" ? cfg.title(item)
    : (cfg.title || `${HUMAN_LABEL(type)}${item.asset_id ? ` · ${item.asset_id}` : ""}`);
  const subtitle = typeof cfg.subtitle === "function" ? cfg.subtitle(item)
    : (cfg.subtitle || (item.created_at ? `Filed ${fmt(item.created_at)}` : null));
  const status = typeof cfg.status === "function" ? cfg.status(item) : cfg.status;

  return (
    <PaperShell testid={`report-doc-${type}`}>
      <DocHeader
        badge={badge}
        title={title}
        subtitle={subtitle}
        site={item.site_code}
        wo={item.work_order_number || null}
        statusChip={status ? <StatusChip label={status.label} tone={status.tone} /> : null}
      />

      <TwoCol>
        <Col testid={`doc-${type}-col-left`}>
          {kvRows.length > 0 && (
            <DocSection icon={ClipboardCheck} title="Details" testid={`doc-${type}-facts`}>
              <KvGrid rows={kvRows} />
            </DocSection>
          )}
          {hazardChips.length > 0 && (
            <DocSection icon={ShieldAlert} title={`Hazards · ${hazardChips.length}`} testid={`doc-${type}-hazards`} dense>
              <ChipRow items={hazardChips} tone="orange" />
            </DocSection>
          )}
          {sigRows.length > 0 && (
            <DocSection icon={PenLine} title="Signatures" testid={`doc-${type}-sigs`} dense>
              <div className="grid grid-cols-1 gap-2">
                {sigRows.map((s) => (
                  <SignatureCard
                    key={s.key}
                    role={HUMAN_LABEL(s.key.replace(/_?sig(nature)?$/i, "")) || "Signed by"}
                    name={item.sign_off_name || item.created_by_name || item.tradesman_name}
                    when={item.created_at}
                    sig={s.value}
                  />
                ))}
              </div>
            </DocSection>
          )}
        </Col>

        <Col testid={`doc-${type}-col-right`}>
          {proseRows.length > 0 && (
            <DocSection icon={ClipboardList} title="Notes" testid={`doc-${type}-notes`}>
              {proseRows.map((p) => (
                <ProseBlock key={p.key} label={p.label} value={p.value} />
              ))}
            </DocSection>
          )}
          {(photosField || markupsField) && (photosField?.length || markupsField?.length) > 0 && (
            <DocSection
              icon={Package}
              title={`Evidence Photos · ${(photosField?.length || 0) + (markupsField?.length || 0)}`}
              testid={`doc-${type}-photos`}
              dense
            >
              <DensePhotoGrid items={{ photos: photosField || [], markups: markupsField || [] }} />
            </DocSection>
          )}
          {sentBackReason && (
            <DocSection icon={AlertTriangle} title="Sent Back" tone="red" testid={`doc-${type}-sendback`}>
              <ProseBlock label="Reason" value={sentBackReason} tone="red" />
            </DocSection>
          )}
        </Col>
      </TwoCol>
    </PaperShell>
  );
}

function rank(order, label) {
  const i = order.indexOf(label);
  return i === -1 ? 999 : i;
}
function formatValue(v) {
  if (v == null) return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v instanceof Date) return fmt(v.toISOString());
  if (typeof v === "string") {
    // ISO date-time detection
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return fmt(v);
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return fmtDateOnly(v);
    return v;
  }
  if (Array.isArray(v)) return v.length + " items";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 80);
  return String(v);
}
